import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SyllabusQuery, SyllabusService, SyllabusStoreModel } from '@examdojo/category/v2';
import { ReviewPermissionsQuery } from '@examdojo/review';
import { first, map } from 'rxjs';
import { ActiveSyllabusManager } from '../storage/active-syllabus-manager';
import { SYLLABUS_SLUG_PARAM } from './validation-routing.model';

function getTargetSyllabus(
  syllabuses: SyllabusStoreModel[],
  syllabusesWithPermissions: number[],
  activeSyllabusManager: ActiveSyllabusManager,
): SyllabusStoreModel {
  // 1. Try last active slug from ActiveSyllabusManager if valid
  const lastActiveSlug = activeSyllabusManager.getActiveSyllabusSlug();
  if (lastActiveSlug) {
    const syllabusFromSlug = syllabuses.find((s) => s.slug === lastActiveSlug);
    if (syllabusFromSlug) {
      const hasPermissions = syllabusesWithPermissions.includes(syllabusFromSlug.id);
      if (hasPermissions || syllabusesWithPermissions.length === 0) {
        return syllabusFromSlug;
      }
    }
  }

  // 2. Fallback to first available with permissions or first syllabus
  const targetId = syllabusesWithPermissions.length > 0 ? syllabusesWithPermissions[0] : syllabuses[0].id;
  return syllabuses.find((s) => s.id === targetId)!;
}

export const syllabusRedirectGuard: CanActivateFn = (route) => {
  const syllabusService = inject(SyllabusService);
  const syllabusQuery = inject(SyllabusQuery);
  const reviewPermissionsQuery = inject(ReviewPermissionsQuery);
  const activeSyllabusManager = inject(ActiveSyllabusManager);
  const router = inject(Router);

  return syllabusService.fetchSyllabuses().pipe(
    first(),
    map((syllabuses) => {
      if (!syllabuses.length) {
        return false;
      }

      const syllabusesWithPermissions = reviewPermissionsQuery.getAllowedSyllabuses();
      const targetSyllabus = getTargetSyllabus(syllabuses, syllabusesWithPermissions, activeSyllabusManager);

      // Check if we're already at a syllabus route with a slug
      const currentSlug = route.paramMap.get(SYLLABUS_SLUG_PARAM);

      if (currentSlug) {
        // If we're already at the target syllabus, allow navigation
        if (currentSlug === targetSyllabus.slug) {
          return true;
        }

        // If we're at a different but valid syllabus with permissions, allow it
        const currentSyllabus = syllabusQuery.getBySlug(currentSlug);
        if (currentSyllabus) {
          const hasPermissions = syllabusesWithPermissions.includes(currentSyllabus.id);
          if (hasPermissions || syllabusesWithPermissions.length === 0) {
            return true;
          }
        }

        // Invalid syllabus or no permissions, redirect to target
        console.debug('Redirecting from invalid syllabus to:', `/syllabus/${targetSyllabus.slug}/validate`);
        return router.createUrlTree(['/syllabus', targetSyllabus.slug, 'validate']);
      }

      // No syllabusSlug in route (empty /syllabus path), redirect to target
      console.debug('Redirecting to:', `/syllabus/${targetSyllabus.slug}/validate`);
      return router.createUrlTree(['/syllabus', targetSyllabus.slug, 'validate']);
    }),
  );
};
